import { useQuery } from "@tanstack/react-query";
import useApiRequest from "./useApiRequest";
import { Company } from "@shared/types/settings";

export interface HookGetSingleCompanyDetailsOptions {
  enabled?: boolean;
  refetchOnMount?: boolean;
  staleTime?: number;
}

type HookGetSingleCompanyDetailsResult = {
  company: Company | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetchCompany: () => void;
};

/**
 * Hook to fetch a single healthcare company's details.
 * Company data is cached indefinitely by default.
 */
export const useGetSingleCompanyDetails = (
  companyId?: string,
  options: HookGetSingleCompanyDetailsOptions = {}
): HookGetSingleCompanyDetailsResult => {
  const { GET } = useApiRequest();
  const {
    enabled = true,
    refetchOnMount = false,
    staleTime = Infinity,
  } = options;

  const {
    data: company,
    error,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery<Company, Error>({
    queryKey: ["healthcare-company", companyId],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }
      return GET<Company>(`/api/healthcare-companies/${companyId}`);
    },
    enabled: enabled && Boolean(companyId),
    refetchOnMount,
    staleTime,
  });

  return {
    company,
    error: error ?? null,
    isLoading,
    isFetching,
    isError,
    refetchCompany: refetch,
  } satisfies HookGetSingleCompanyDetailsResult;
};
